import { Switch, Select, Button } from '@yaradigitallabs/ahua-react';
import { useEffect, useState } from 'react';
import Drawer from '@/components/Drawer';
import TimeFilter from '../../components/time-filter';
import { toTimeInputValue } from '../../utils/helpers';
import type { ProductionPeriodFormSubmitPayload } from '../../utils/types';

type SelectProductionPeriodProps = {
  selectedProductionPeriod: any | null;
  onSelectProductionPeriod: (period: ProductionPeriodFormSubmitPayload) => void;
  outputMaterialList?: any[];
  isDrawerOpen?: boolean;
  setIsDrawerOpen?: (isOpen: boolean) => void;
  isCreating: boolean;
};

const SelectProductionPeriod: React.FC<SelectProductionPeriodProps> = ({
  selectedProductionPeriod,
  onSelectProductionPeriod,
  outputMaterialList,
  isDrawerOpen = false,
  setIsDrawerOpen = () => {},
  isCreating = false,
}) => {
  const isEditMode = Boolean(selectedProductionPeriod);
  const isLoading = isCreating;
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [is24hProduction, setIs24hProduction] = useState(false);
  const isTimeRangeInvalid = !!(startTime && endTime && endTime <= startTime);
  const isConfirmDisabled =
    !startTime ||
    !endTime ||
    isTimeRangeInvalid ||
    (!isEditMode && !selectedMaterialId);

  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }

    if (!selectedProductionPeriod) {
      setStartTime('');
      setEndTime('');
      setSelectedMaterialId('');
      setIs24hProduction(false);
      return;
    }

    setStartTime(
      toTimeInputValue(
        selectedProductionPeriod.start_time ??
          selectedProductionPeriod.startTime
      )
    );
    setEndTime(
      toTimeInputValue(
        selectedProductionPeriod.end_time ?? selectedProductionPeriod.endTime
      )
    );
    setIs24hProduction(
      Boolean(selectedProductionPeriod.is_24_hour_production ?? false)
    );
    setSelectedMaterialId(
      selectedProductionPeriod.material_code ??
        selectedProductionPeriod.materialId ??
        ''
    );
  }, [isDrawerOpen, selectedProductionPeriod]);

  const handleSelectProductionPeriod = () => {
    const periodData: ProductionPeriodFormSubmitPayload = isEditMode
      ? {
          startTime: startTime,
          endTime: endTime,
          is_24_hour_production: false,
        }
      : {
          startTime: startTime,
          endTime: endTime,
          materialId: selectedMaterialId,
          is_24_hour_production: false,
        };

    onSelectProductionPeriod(periodData);
    if (!isEditMode) {
      setStartTime('');
      setEndTime('');
      setSelectedMaterialId('');
      setIs24hProduction(false);
    }
  };

  const handleIs24hProduction = (checked: boolean) => {
    setIs24hProduction(checked);
    if (checked) {
      setStartTime('00:00');
      setEndTime('23:59');
    } else {
      setStartTime('');
      setEndTime('');
    }
  };

  return (
    <Drawer
      isOpen={isDrawerOpen}
      onClose={() => setIsDrawerOpen(false)}
      title={
        isEditMode ? 'Edit production period' : 'Add new production period'
      }
      position="right"
      widthClass="w-[500px]"
      footer={
        <div className="flex justify-between gap-3 w-full">
          <Button
            css={{ width: '100%' }}
            disabled={isLoading}
            size="s"
            variant="primary"
            colorConcept="inverse"
            onClick={() => {
              setStartTime('');
              setEndTime('');
              setSelectedMaterialId('');
              setIs24hProduction(false);
            }}
          >
            Clear
          </Button>
          <Button
            css={{ width: '100%' }}
            disabled={isConfirmDisabled}
            size="s"
            variant="primary"
            colorConcept="brand"
            onClick={() => handleSelectProductionPeriod()}
            loading={isCreating}
            loadingPosition="leading"
          >
            Confirm
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className=" flex justify-start items-center">
          <Switch
            label="24-hour production"
            labelPosition="right"
            checked={is24hProduction}
            onCheckedChange={handleIs24hProduction}
          />
        </div>
        <div className="flex flex-row gap-3 w-full">
          <TimeFilter
            selectedTime={startTime}
            onTimeChange={(value: string) => setStartTime(value)}
            label="Start time"
            disabled={is24hProduction}
          />
          <TimeFilter
            selectedTime={endTime}
            onTimeChange={(value: string) => setEndTime(value)}
            label="End time"
            disabled={is24hProduction}
          />
        </div>
        {isTimeRangeInvalid && (
          <p className="text-xs text-red-500">
            End time must be later than start time.
          </p>
        )}
        <Select
          label="Material number and description"
          value={selectedMaterialId}
          disabled={isEditMode}
          css={{ width: '100%' }}
          //className={`font-noto-sans`}
          className={`font-noto-sans ${isEditMode ? '[&_button]:bg-gray-100! [&_label:nth-of-type(2)]:text-black!' : ''}`.trim()}
          size="n"
          items={outputMaterialList?.map(material => ({
            text: `${material.material_code} - ${material.description}`,
            value: material.material_code,
          }))}
          readOnly={isEditMode}
          onChange={setSelectedMaterialId}
        />
      </div>
    </Drawer>
  );
};

export default SelectProductionPeriod;
